/**
 * CancelSessionModal — the shared reason-dropdown cancel flow for a production session (WCO-2706).
 *
 * Rendered by the supervisor/manager cancel paths so they can't drift apart:
 *   Session Details footer button, Manager Guide session list, Manager Schedule View.
 *   (NOTE: ProductionPlanning.tsx still has its own inline cancel modal calling
 *   productionService.cancelSession directly — a known remaining direct caller to migrate.)
 *
 * Flow: pick a reason (dropdown, "Other" reveals a text field) → cancel.
 *
 * Authorization is the api-gateway role gate: `POST /api/production/sessions/:id/cancel` is
 * restricted to supervisor/manager/admin, so an operator token never reaches the route. On top of
 * that, product-service re-checks the session status and no longer accepts `force` at all
 * (WCO-2706). The status list below gates the UI only — the server enforces the same rules
 * independently, so a regressing call site cannot cancel a running session.
 */
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import productionService from '../services/productionService';
import { CANCEL_REASONS } from '../utils/cancelReasons';
import { isSessionCancellable } from '../utils/sessionStatus';
import { showTransferReminderToast } from './TransferReminderToast';

// The cancellable-status allow-list is NOT re-exported from here. It lives in
// `utils/sessionStatus` (a thin adapter over @tnb/session-status, the same list the cancel route
// enforces) and every consumer imports it from there — a component must not double as the barrel
// for a shared predicate, which is exactly the kind of drift WCO-2706 set out to remove.

interface CancelSessionModalProps {
  /** Session to cancel; null/undefined keeps the modal closed. */
  session: { session_number: string; product_name?: string; status?: string } | null;
  /** Dismiss without cancelling. */
  onClose: () => void;
  /** Fired after a successful cancellation (in addition to the internal cache invalidation). */
  onCancelled?: () => void;
}

const CancelSessionModal: React.FC<CancelSessionModalProps> = ({ session, onClose, onCancelled }) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [otherReason, setOtherReason] = useState('');

  const reset = () => {
    setReason('');
    setOtherReason('');
  };

  const cancelMutation = useMutation(
    ({ cancelReason }: { cancelReason: string }) =>
      productionService.cancelSession(session!.session_number, cancelReason),
    {
      onSuccess: (data: any) => {
        queryClient.invalidateQueries(['productionSessions']);
        queryClient.invalidateQueries(['sub-sessions', session?.session_number]);
        queryClient.invalidateQueries(['supervisor-stations']);
        queryClient.invalidateQueries(['cancelled-transfer-reminders']);
        toast.success(t('sessionDetails.sessionCancelled'));

        // Persistent reminder when the cancelled session still has transfers to delete in VB6
        const transfers = data?.submitted_transfers;
        if (transfers && transfers.length > 0) {
          const transferNumbers = transfers.map((tr: any) => tr.transfer_number).join(', ');
          const label = t('sessionDetails.vb6TransferReminder') || 'Remember to delete transfer in VB6';
          const detail = `${t('sessionDetails.vb6TransferDetails') || 'Transfer'}: ${transferNumbers}`;
          showTransferReminderToast(label, detail);
        }

        reset();
        onClose();
        onCancelled?.();
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || error?.message || t('sessionDetails.failedToCancel'));
      },
    }
  );

  if (!session) return null;

  const isOther = reason === 'other';
  const resolvedReason = isOther ? otherReason.trim() : reason;
  const canConfirm = !!resolvedReason;

  const handleClose = () => {
    reset();
    onClose();
  };

  /**
   * Status rule, checked HERE rather than trusted from the call site (WCO-2706 review).
   * Every screen that opens this modal already hides its cancel button for running sessions,
   * but that guard lives in four places and a fifth caller could forget it — this component
   * promises in its own docstring that running sessions are never cancelled, so it enforces it.
   *
   * An UNKNOWN status is deliberately allowed through: ManagerScheduleView synthesizes a
   * `{ session_number }` object with no status when the session is not in its loaded list, and
   * blocking that would break a legitimate cancel. Nothing is lost by it — the cancel route
   * re-checks the status under FOR UPDATE and rejects a running session no matter what the UI does.
   */
  const blockedStatus = session.status && !isSessionCancellable(session.status) ? session.status : null;
  const notCancellableMessage = blockedStatus
    ? t('sessionDetails.sessionNotCancellable').replace('{{status}}', blockedStatus)
    : '';

  if (blockedStatus) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {t('sessionDetails.cancelSession')}
          </h3>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
            {session.session_number}
            {session.product_name ? ` — ${session.product_name}` : ''}
          </p>
          <p className="text-sm text-red-600 dark:text-red-400 mb-4">{notCancellableMessage}</p>
          <div className="flex justify-end">
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {t('sessionDetails.cancelSession')}
            </h3>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
              {session.session_number}
              {session.product_name ? ` — ${session.product_name}` : ''}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {t('sessionDetails.cancelSessionDesc')}
            </p>

            <select
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (e.target.value !== 'other') setOtherReason('');
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">{t('sessionDetails.selectCancelReason')}</option>
              {CANCEL_REASONS.map((code) => (
                <option key={code} value={code}>{t(`sessionDetails.cancelReasons.${code}`)}</option>
              ))}
            </select>

            {isOther && (
              <input
                type="text"
                autoFocus
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                placeholder={t('sessionDetails.cancelReasonPlaceholder')}
                className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
              />
            )}

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => {
                  // Re-check at confirm time: the session may have started running while the
                  // modal sat open (the early return above only covers the status at render).
                  if (session.status && !isSessionCancellable(session.status)) {
                    toast.error(t('sessionDetails.sessionNotCancellable').replace('{{status}}', session.status));
                    return;
                  }
                  if (!canConfirm) { toast.error(t('sessionDetails.provideCancelReason')); return; }
                  cancelMutation.mutate({ cancelReason: resolvedReason });
                }}
                disabled={!canConfirm || cancelMutation.isLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {cancelMutation.isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('sessionDetails.confirmCancel')}
              </button>
            </div>
          </div>
        </div>
  );
};

export default CancelSessionModal;
